/**
 * Clark Tanks - Expivi 3D Modal
 *
 * Single button on PDP:
 *   "View in 3D" → full-screen modal, all options (Choose Tank / Colors / Pumps)
 *   (labelled "Build in 3D" on Kingspan, where the customer really does build one)
 *
 * Preset mapping is defined in expivi-clark-config.js.
 * The configurator URL is read from the hidden Expivi app-proxy link on the page.
 * CSS is injected into the srcdoc via expivi-clark.css.
 *
 * (Cloned from the Kingspan integration. The "View in 3D" windowed path and the
 * Save Design → Make.com webhook remain from that base but are dormant for Clark:
 * the single button uses fullscreen mode, and the Clark quote/lead flow is a
 * separate phase — see the webhook TODO below.)
 */
(function () {
  'use strict';

  // Guard: this script is loaded globally from theme.liquid (via the modal shell)
  // AND, on the Slimline template, also by a per-template Custom Liquid block.
  // Run the wiring only once if both are present.
  if (window.__expivi3dModalLoaded) return;
  window.__expivi3dModalLoaded = true;

  /* Save Design was hidden while Clark could not send from their own domain: the
     mails went out through the Kingspan sending setup, which was fine for our
     testing and wrong for a customer-facing button, because neither the sender
     nor the reply-to would have been Clark's.

     Their IT has since set that up. The Make scenario now sends over the
     connection "Clark Tanks - Mailgun (SMTP noreply@mail.clarktanks.com.au)",
     so the mail leaves Clark's own domain with its SPF and DKIM — verified in
     the scenario before flipping this. */
  var SHOW_SAVE_DESIGN = true;

  // Self-inject the "View in 3D" / "Build in 3D" buttons when the template did not
  // render them. The modal shell is global (all products tagged "3d"), but the buttons
  // used to live in a per-template Custom Liquid block that only the Slimline template
  // had — so Modline/Square/Round got the modal but no way to open it. Injecting here
  // makes every tagged product work with zero per-template setup. Idempotent: if the
  // template already printed the buttons (Slimline), we leave them and just bind below.
  injectButtonsIfMissing();

  function injectButtonsIfMissing() {
    if (document.getElementById('expivi-build-3d-trigger')) return; // already rendered by template
    if (!document.getElementById('expivi-3d-modal')) return;  // not a 3d product page
    // Anchor: the Dawn price block, so the buttons sit right under the price (same spot
    // the Slimline template places them).
    var priceBlock = document.querySelector('.product__info-container [id^="price-template--"]')
                  || document.querySelector('[id^="price-template--"]')
                  || document.querySelector('.product__info-container .price');
    if (!priceBlock) return;
    var row = document.createElement('div');
    row.className = 'expivi-buttons-row';
    row.innerHTML =
      '<button id="expivi-build-3d-trigger" class="expivi-btn expivi-btn--primary" type="button">' +
        '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>' +
        // "View in 3D", not Kingspan's "Build in 3D": a Clark customer picks a tank
        // from the range and looks at it, they do not build one from scratch
        // (Ivan Siah, meeting 2026-07-30).
        'View in 3D' +
      '</button>';
    priceBlock.insertAdjacentElement('afterend', row);
  }

  var modal        = document.getElementById('expivi-3d-modal');
  var buildTrigger = document.getElementById('expivi-build-3d-trigger');
  var viewTrigger  = document.getElementById('expivi-3d-trigger');
  var closeBtn     = document.getElementById('expivi-modal-close');
  var iframe       = document.getElementById('expivi-configurator-iframe');
  var loadingEl    = document.getElementById('expivi-loading');
  var modalInner   = modal ? modal.querySelector('.expivi-modal') : null;

  if (!modal || !iframe) return;

  // Read configurator URL from the existing Expivi "Configure" button.
  // Strip the "currency" param — it triggers a /services/currency/update redirect
  // that returns 404 on Shopify preview domains.
  var configureLink = document.querySelector('a[href*="/apps/configurator"]');
  var baseUrl = '';
  if (configureLink) {
    try {
      var _u = new URL(configureLink.getAttribute('href'), window.location.origin);
      _u.searchParams.delete('currency');
      baseUrl = _u.pathname + (_u.search ? _u.search : '');
    } catch (e) {
      baseUrl = configureLink.getAttribute('href');
    }
  }
  var cssUrl        = modal.getAttribute('data-css-url') || '';
  var productId     = parseInt(modal.getAttribute('data-product-id'), 10);

  // Fallback for templates that don't render the hidden link: point the iframe at THIS
  // product's own app-proxy URL. It must be this product and never a fixed one — the
  // Expivi app creates the cart variant on whichever product the proxy URL names, so a
  // hardcoded id quietly routes every tank's configuration into that product's cart line
  // and grows its variant list. Leave baseUrl empty when there is no product id: openModal
  // already warns and bails, which beats opening a different tank's configurator.
  if (!baseUrl && productId) baseUrl = '/apps/configurator?catalogue=' + productId;

  // Resolve preset for this product from config file
  var config  = (window.ClarkExpiviConfig || {}).products || {};
  var preset  = (config[productId] || {}).preset || '';

  // Clark models a tank+pump bundle by putting BOTH SKUs in the one Shopify SKU
  // field, comma separated — the tank first, the accessory second:
  //   "CT5000, APRRHMS52"      -> CT5000    (22700L round tank + pump)
  //   "CTSL2000, APRPRJ55E"    -> CTSL2000  (slimline + pump)
  // Only the tank has an Expivi preset, so take the first segment. Kingspan
  // expressed the same idea as a "-TP" suffix, which deriveBasePresetSku()
  // already strips; this is the comma-separated flavour of it.
  // Left untouched: a plain SKU has no comma and passes straight through.
  // Also drop ALL whitespace: some SKUs are typed with a space before the
  // variant word ("CT5200 Squat") while the Expivi preset identifier is one
  // token ("ct5200squat"). Verified against the API: "CT5200 Squat" 404s,
  // "CT5200Squat" resolves. Slugs never contain spaces, so removing them is
  // safe here and saves a presetBySku entry per squat product.
  function tankSkuFromBundle(s) {
    if (!s) return '';
    return s.split(',')[0].replace(/\s+/g, '');
  }

  // Derive the base preset identifier from a SKU.
  // Product SKUs follow the convention: PREFIX-<numeric dimension groups>[-LETTER_SUFFIX...]
  // The Expivi preset is named exactly as the base (prefix + numeric groups), so we strip any
  // trailing letter suffix (e.g. -FT fire tank, -TP tank+pump bundle, or any future suffix).
  //   SL-1150-3300-2020      -> SL-1150-3300-2020   (already base, 1:1)
  //   SL-1150-3300-2020-TP   -> SL-1150-3300-2020
  //   ML-1150-4000-2250-FT   -> ML-1150-4000-2250
  //   RD-2700-1860-FT        -> RD-2700-1860        (round: PREFIX-diameter-height)
  // NOTE: Clark's own SKUs carry no hyphens ("CT3000", "CT1100SQUAT"), so for
  // them this is a pass-through; it still matters for Kingspan-style SKUs.
  function deriveBasePresetSku(s) {
    if (!s) return '';
    var seg = s.split('-');
    var out = [seg[0]];                              // prefix (letters)
    for (var i = 1; i < seg.length; i++) {
      if (/^\d+$/.test(seg[i])) out.push(seg[i]);    // keep numeric dimension groups
      else break;                                    // first non-numeric segment -> suffix starts
    }
    return out.join('-');
  }

  // SKU-based preset preload. Resolution order:
  //   1) presetBySku[<SKU>]      — explicit override for SKUs that break the naming convention
  //                                (checked against the raw SKU first, then the tank SKU, so a
  //                                 bundle can be overridden either way)
  //   2) deriveBasePresetSku()   — on the tank SKU: drops the bundled accessory and any
  //                                -FT/-TP style suffix, leaving the base SKU == preset name
  //   3) preset                  — legacy per-product fallback (above)
  // The Expivi lookup is case-insensitive (verified: ct3000 / CT3000 / Ct3000 all resolve to
  // the ct3000 preset), so the uppercase Shopify SKUs need no folding.
  var sku         = (modal.getAttribute('data-sku') || '').trim();
  var tankSku     = tankSkuFromBundle(sku);
  var presetBySku = (window.ClarkExpiviConfig || {}).presetBySku || {};
  var presetKey   = (sku && presetBySku[sku]) ||
                    (tankSku && presetBySku[tankSku]) ||
                    deriveBasePresetSku(tankSku) ||
                    preset;

  // ─── Preset loading ───────────────────────────────────────

  function applyPreset(presetKey) {
    var win = iframe.contentWindow;
    var attempts = 0;
    var maxAttempts = 60; // 60 × 500ms = 30s

    var interval = setInterval(function () {
      attempts++;
      try {
        var apiReady   = win && win.expivi && typeof win.expivi.loadConfiguration === 'function';
        var hasOnReady = apiReady && win.expivi._events && win.expivi._events.onReady;

        if (hasOnReady) {
          clearInterval(interval);

          var sub = win.expivi._events.onReady.subscribe(function () {
            if (sub) sub.unsubscribe();

            var apiToken = win.expivi.apiToken;
            if (!presetKey || !apiToken) return;

            // Direct preset lookup by identifier (the SKU). Using find/{slug} instead of the
            // catalogue ?include=presets map avoids pagination once there are many presets (~81).
            fetch('https://expivi.net/api/product_preset/find/' + encodeURIComponent(presetKey) + '?include=constraints', {
              headers: { 'Authorization': 'Bearer ' + apiToken }
            })
              .then(function (r) {
                if (!r.ok) throw new Error('HTTP ' + r.status);
                return r.json();
              })
              .then(function (d) {
                var cfg = d && d.data && d.data.attributes && d.data.attributes.configuration;
                if (!cfg) {
                  console.warn('Expivi: No preset found for "' + presetKey + '"');
                  return;
                }
                return win.expivi.loadConfiguration(cfg);
              })
              .catch(function (err) {
                console.warn('Expivi: Failed to apply preset:', err);
              });
          });

        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          console.warn('Expivi: API not ready after timeout');
        }
      } catch (e) {
        if (attempts >= maxAttempts) clearInterval(interval);
      }
    }, 500);
  }

  // ─── Load saved configuration by bundle UUID ────────────
  // Fetches the saved config JSON from DigitalOcean Spaces and applies it via loadConfiguration().

  var SAVED_CONFIG_BASE = 'https://product-configurator.ams3.cdn.digitaloceanspaces.com/clark/';

  function loadSavedConfiguration(bundleUuid) {
    var win = iframe.contentWindow;
    var attempts = 0;
    var maxAttempts = 60; // 60 × 500ms = 30s

    var interval = setInterval(function () {
      attempts++;
      try {
        var apiReady   = win && win.expivi && typeof win.expivi.loadConfiguration === 'function';
        var hasOnReady = apiReady && win.expivi._events && win.expivi._events.onReady;

        if (hasOnReady) {
          clearInterval(interval);

          var sub = win.expivi._events.onReady.subscribe(function () {
            if (sub) sub.unsubscribe();

            // Cache-bust. Saving again from an already-restored page reuses the same
            // bundle_uuid, so the JSON is overwritten under the SAME filename — but the
            // DigitalOcean CDN serves it with max-age=3600, so for an hour the restore
            // replayed the PREVIOUS design. Observed: origin had the new colour at
            // 10:23 while the CDN still returned the 10:00 copy.
            fetch(SAVED_CONFIG_BASE + bundleUuid + '.json?t=' + Date.now(), { cache: 'no-store' })
              .then(function (r) {
                if (!r.ok) throw new Error('HTTP ' + r.status);
                return r.json();
              })
              .then(function (config) {
                return win.expivi.loadConfiguration(config);
              })
              .catch(function (err) {
                console.warn('Expivi: Failed to load saved configuration:', err);
              });
          });

        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          console.warn('Expivi: API not ready after timeout');
        }
      } catch (e) {
        if (attempts >= maxAttempts) clearInterval(interval);
      }
    }, 500);
  }

  // ─── Late style overrides (injected after Vuetify) ───────
  // Vuetify injects <style> tags dynamically after onload.
  // We watch <head> for new additions and always re-append our override last.

  function injectLateStyles(doc, win) {
    if (!doc) return;

    function appendOverride() {
      var old = doc.getElementById('ksp-late-styles');
      if (old) old.remove();
      var s = doc.createElement('style');
      s.id = 'ksp-late-styles';
      s.textContent = [
        /* Step buttons gold — specificity 0-3-0 to beat Vuetify .v-application .primary--text */
        '.v-application .step-btn-next.v-btn, .v-application .step-btn-prev.v-btn { color: #FFC320 !important; }',
        /* Info icon container — match WooCommerce reference rgba(0,0,0,0.38) */
        '.step-header-icon { color: rgba(0, 0, 0, 0.38) !important; }',
        /* Original hidden ATC button */
        '.btn.expivi-add-to-cart { background-color: #304C33 !important; color: #fff !important; border-radius: 30px !important; border: none !important; }',
        /* Custom bottom bar buttons — explicit styles to avoid inheritance */
        '.ksp-btn { display:inline-flex !important; align-items:center !important; justify-content:center !important; padding:0 20px !important; height:40px !important; border-radius:30px !important; font-size:14px !important; font-weight:600 !important; cursor:pointer !important; white-space:nowrap !important; text-decoration:none !important; }',
        '.ksp-btn--primary { background-color:#304C33 !important; color:#fff !important; border:2px solid #304C33 !important; }',
        '.ksp-btn--outline { background-color:transparent !important; color:#304C33 !important; border:2px solid #304C33 !important; }',
        '.ksp-btn--outline:hover { background-color:#304C33 !important; color:#fff !important; }'
      ].join('\n');
      doc.head.appendChild(s);
    }

    appendOverride();

    // Re-append whenever Vuetify injects a new <style> into <head>
    var headObs = new win.MutationObserver(function (mutations) {
      var hasNew = mutations.some(function (m) {
        return Array.from(m.addedNodes).some(function (n) {
          return n.nodeName === 'STYLE' && n.id !== 'ksp-late-styles';
        });
      });
      if (hasNew) appendOverride();
    });
    headObs.observe(doc.head, { childList: true });

    // Stop watching after 15s (Vuetify is done by then)
    win.setTimeout(function () { headObs.disconnect(); }, 15000);
  }

  // ─── USP row injection ────────────────────────────────────

  function injectUspRow(doc) {
    if (!doc) return;
    if (doc.querySelector('.usp-row')) return;
    // Insert inside configurator-container, at the bottom
    var container = doc.querySelector('.configurator-container');
    if (!container) return;
    var usp = doc.createElement('div');
    usp.className = 'usp-row';
    usp.innerHTML = [
      // Wording confirmed with the Clark team; the asterisks are theirs — the
      // conditions live on the site, not here.
      '<div class="usp-row-column"><b>Australian Made</b></div>',
      '<hr>',
      '<div class="usp-row-column">25 Years Warranty*</div>',
      '<hr>',
      '<div class="usp-row-column">Delivery Included*</div>'
    ].join('');
    container.appendChild(usp);
  }

  // ─── Bottom bar injection (price + qty + ATC + Save Design) ──────

  // The app creates the configured variant and calls /cart/add.js in the same second,
  // before the storefront knows the variant exists → 422 "... is already sold out".
  // The same request succeeds a few seconds later, so remember the rejected body and
  // let the Add to Cart poll replay it. Watches the iframe's XHR and fetch once.
  var SOLD_OUT_RETRY_EVERY_MS = 2000;

  function isCartAdd(url) {
    return /\/cart\/add(\.js)?(\?|$)/.test(String(url || ''));
  }

  function isSoldOutRejection(status, text) {
    return status === 422 && /sold out/i.test(String(text || ''));
  }

  function watchSoldOutCartAdds(win) {
    if (win.__kspSoldOutWatch) return win.__kspSoldOutWatch;
    var watch = { body: null };
    win.__kspSoldOutWatch = watch;

    var proto = win.XMLHttpRequest && win.XMLHttpRequest.prototype;
    if (proto) {
      var origOpen = proto.open;
      var origSend = proto.send;
      proto.open = function (method, url) {
        this.__kspCartAdd = isCartAdd(url);
        return origOpen.apply(this, arguments);
      };
      proto.send = function (body) {
        var xhr = this;
        if (xhr.__kspCartAdd) {
          xhr.addEventListener('loadend', function () {
            if (isSoldOutRejection(xhr.status, xhr.responseText)) watch.body = body;
          });
        }
        return origSend.apply(this, arguments);
      };
    }

    if (win.fetch) {
      var origFetch = win.fetch;
      win.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : input && input.url;
        var result = origFetch.apply(this, arguments);
        if (!isCartAdd(url) || !init || typeof init.body !== 'string') return result;
        return result.then(function (res) {
          if (res.status !== 422) return res;
          res.clone().text().then(function (text) {
            if (isSoldOutRejection(res.status, text)) watch.body = init.body;
          });
          return res;
        });
      };
    }
    return watch;
  }

  function replaySoldOutCartAdd(watch) {
    var body = watch.body;
    watch.body = null;
    var isJson = /^\s*\{/.test(body);
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': isJson ? 'application/json' : 'application/x-www-form-urlencoded; charset=UTF-8',
        Accept: 'application/json'
      },
      body: body
    }).then(function (res) {
      if (res.status !== 422) return;
      return res.text().then(function (text) {
        if (isSoldOutRejection(res.status, text)) watch.body = body;
      });
    }).catch(function () { watch.body = body; });
  }

  // The app keys variants by adler32(price) and reuses the first variant carrying that
  // code without checking its price. adler32 of a 4-digit price collides constantly
  // ("3880" and "4690" share 021800d4), so a configuration can land on a variant priced
  // for a different one and Shopify charges that variant's price. The total Expivi shows
  // is the configured price, so compare it with the cart line and refuse a mismatch.
  function readConfiguratorTotalCents(doc) {
    var el = doc.querySelector('.expivi-product-price');
    if (!el) return null;
    // first money figure in the element, e.g. "$4,560.00" → 456000
    var m = String(el.textContent || '').match(/(\d[\d,]*)(?:\.(\d{1,2}))?/);
    if (!m) return null;
    var num = parseFloat(m[1].replace(/,/g, '') + '.' + (m[2] || '0'));
    return isNaN(num) ? null : Math.round(num * 100);
  }

  function formatCents(cents) {
    return '$' + (cents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function removeCartLine(line, previousQty) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: line.key, quantity: previousQty })
    });
  }

  function showPriceMismatchNotice(doc, expectedCents, chargedCents) {
    if (!doc || doc.getElementById('ksp-price-notice')) return;
    var ov = doc.createElement('div');
    ov.id = 'ksp-price-notice';
    ov.style.cssText = 'position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;padding:20px;font-family:Lato,Helvetica,Arial,sans-serif;';
    ov.innerHTML = [
      '<div style="background:#fff;border-radius:12px;max-width:440px;width:100%;padding:30px;">',
      '  <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">',
      '    <span style="font-family:Lato,serif;font-size:22px;font-weight:900;color:#304C33;">We could not add this tank</span>',
      '    <span id="ksp-price-notice-close" style="cursor:pointer;font-size:14px;color:#4B4F58;font-weight:600;">CLOSE</span>',
      '  </div>',
      '  <p style="color:#4B4F58;font-size:15px;line-height:1.6;margin:0 0 12px;">The price returned by the store (' + formatCents(chargedCents) + ') does not match your configuration (' + formatCents(expectedCents) + '), so nothing was added to your cart.</p>',
      '  <p style="color:#4B4F58;font-size:15px;line-height:1.6;margin:0;">Please use <strong>Save Design</strong> and our team will get back to you with this exact configuration.</p>',
      '</div>'
    ].join('');
    doc.body.appendChild(ov);
    doc.getElementById('ksp-price-notice-close').addEventListener('click', function () {
      ov.parentNode.removeChild(ov);
    });
  }

  function injectBottomBar(doc, win) {
    if (!doc) return;
    if (doc.querySelector('.ksp-bottom-bar')) return;

    var bottomContent = doc.querySelector('.bottom-content');
    if (!bottomContent) return;

    // Build the bar — two rows matching WooCommerce reference:
    //   Row 1: Expivi price (moved here natively)
    //   Row 2: qty selector + Add to Cart + Save Design
    var bar = doc.createElement('div');
    bar.className = 'ksp-bottom-bar';
    bar.innerHTML = [
      '<div class="ksp-bottom-price"></div>',
      '<div class="ksp-bottom-actions">',
      '  <div class="ksp-qty-wrap">',
      '    <button class="ksp-qty-btn" id="ksp-qty-minus" type="button">&#x2212;</button>',
      '    <input  class="ksp-qty-input" id="ksp-qty-value" type="number" value="1" min="1" max="99">',
      '    <button class="ksp-qty-btn" id="ksp-qty-plus"  type="button">&#x2B;</button>',
      '  </div>',
      '  <button class="ksp-btn ksp-btn--primary" id="ksp-add-to-cart"  type="button">Add to Cart</button>',
      SHOW_SAVE_DESIGN
        ? '  <button class="ksp-btn ksp-btn--outline"  id="ksp-save-design" type="button">Save Design</button>'
        : '',
      '</div>'
    ].join('');

    // Hide the original bottom-content (inside viewer)
    bottomContent.style.display = 'none';

    // Append bar to the configurator-container (right panel), after USP row if present
    var confContainer = doc.querySelector('.configurator-container');
    if (confContainer) {
      confContainer.appendChild(bar);
    } else {
      bottomContent.parentElement.insertBefore(bar, bottomContent);
    }

    // Move Expivi's own price element into the price row so Expivi keeps managing it
    var priceEl = doc.querySelector('.expivi-product-price');
    if (priceEl) {
      bar.querySelector('.ksp-bottom-price').appendChild(priceEl);
    }

    // On mobile (<=600px) the qty stepper + both buttons don't fit on one row, so
    // dock the qty next to the price (top row) and leave the buttons full-width
    // below. On larger screens keep the original single actions row untouched.
    var qtyWrap = bar.querySelector('.ksp-qty-wrap');
    var priceRow = bar.querySelector('.ksp-bottom-price');
    var actionsRow = bar.querySelector('.ksp-bottom-actions');
    var mq = win.matchMedia('(max-width:600px)');
    function placeQty(m) {
      if (m.matches) priceRow.appendChild(qtyWrap);
      else actionsRow.insertBefore(qtyWrap, actionsRow.firstChild);
    }
    placeQty(mq);
    if (mq.addEventListener) mq.addEventListener('change', placeQty);
    else if (mq.addListener) mq.addListener(placeQty);

    // Quantity +/-
    bar.querySelector('#ksp-qty-minus').addEventListener('click', function () {
      var inp = bar.querySelector('#ksp-qty-value');
      var v = parseInt(inp.value, 10) || 1;
      if (v > 1) inp.value = v - 1;
    });
    bar.querySelector('#ksp-qty-plus').addEventListener('click', function () {
      var inp = bar.querySelector('#ksp-qty-value');
      var v = parseInt(inp.value, 10) || 1;
      inp.value = v + 1;
    });

    // Add to Cart.
    // 1) Show Summary modal listing the configuration (mirrors WooCommerce reference);
    //    only proceed to the real cart add when the user confirms.
    // 2) Expivi's native add-to-cart ALWAYS adds quantity 1 and ignores our stepper, and
    //    its post-add redirect to "/cart" happens INSIDE the srcdoc iframe (blocked by CSP
    //    frame-ancestors 'none' → broken page). So we:
    //      a) snapshot the cart, b) fire the native add (adds 1 of the configured replica),
    //      c) poll until the new/updated line appears, d) correct that line's quantity to the
    //         chosen amount via /cart/change.js, e) hand off to Shopify (send TOP window to /cart).
    //    An "Adding to cart…" overlay covers the few seconds of replica creation.
    bar.querySelector('#ksp-add-to-cart').addEventListener('click', function () {
      var addBtn = bar.querySelector('#ksp-add-to-cart');
      if (addBtn.getAttribute('data-busy') === '1') return;
      var qty = parseInt(bar.querySelector('#ksp-qty-value').value, 10) || 1;

      showSummaryModal(doc, win, function proceedToCart() {
        addBtn.setAttribute('data-busy', '1');
        showAddingOverlay(doc);

        function snapshot() {
          return fetch('/cart.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (c) {
              var m = {}; (c.items || []).forEach(function (it) { m[it.key] = it.quantity; });
              return { count: c.item_count || 0, map: m };
            })
            .catch(function () { return { count: 0, map: {} }; });
        }
        function goToCart() { (window.top || window).location.href = '/cart'; }
        function fail() { hideAddingOverlay(doc); addBtn.removeAttribute('data-busy'); }

        snapshot().then(function (before) {
          var expectedCents = readConfiguratorTotalCents(doc);
          var soldOut = watchSoldOutCartAdds(win);
          soldOut.body = null;
          var origBtn = doc.querySelector('.expivi-add-to-cart');
          if (origBtn) origBtn.click();
          var tries = 0;
          var lastReplayAt = 0;
          var poll = setInterval(function () {
            tries++;
            fetch('/cart.js', { headers: { Accept: 'application/json' } })
              .then(function (r) { return r.json(); })
              .then(function (c) {
                if (!c || c.item_count <= before.count) {
                  if (tries >= 60) { clearInterval(poll); fail(); } // ~30s safety net
                  else if (soldOut.body && Date.now() - lastReplayAt >= SOLD_OUT_RETRY_EVERY_MS) {
                    lastReplayAt = Date.now();
                    replaySoldOutCartAdd(soldOut);
                  }
                  return;
                }
                clearInterval(poll);
                // the line the native add created/updated = the one whose qty grew
                var target = null;
                (c.items || []).forEach(function (it) {
                  if (it.quantity > (before.map[it.key] || 0)) target = it;
                });
                if (target && expectedCents !== null && target.price !== expectedCents) {
                  console.error('Cart line price ' + target.price + ' differs from configured ' + expectedCents + ' (variant ' + target.variant_id + '); removing it');
                  removeCartLine(target, before.map[target.key] || 0)
                    .catch(function () {})
                    .then(function () {
                      fail();
                      showPriceMismatchNotice(doc, expectedCents, target.price);
                    });
                  return;
                }
                if (target && qty > 1) {
                  // native already added 1 → set absolute total = previous + chosen qty
                  var desired = (before.map[target.key] || 0) + qty;
                  fetch('/cart/change.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: JSON.stringify({ id: target.key, quantity: desired })
                  }).then(goToCart).catch(goToCart);
                } else {
                  goToCart();
                }
              })
              .catch(function () {
                if (tries >= 60) { clearInterval(poll); fail(); }
              });
          }, 500);
        });
      });
    });

    // Save Design — open modal form. Absent while SHOW_SAVE_DESIGN is off, and
    // binding to a null would throw here and take the rest of the wiring with it.
    var saveBtn = bar.querySelector('#ksp-save-design');
    if (saveBtn) {
      saveBtn.addEventListener('click', function () {
        openSaveDesignModal(doc, win);
      });
    }
  }

  // ─── Save Design modal ────────────────────────────────────
  // Form collects user data, then calls expivi.saveConfiguration()
  // and shows a Congratulations message.

  function openSaveDesignModal(doc, win) {
    // Don't re-inject if already open
    if (doc.getElementById('ksp-save-modal')) {
      doc.getElementById('ksp-save-modal').style.display = 'flex';
      return;
    }

    var overlay = doc.createElement('div');
    overlay.id = 'ksp-save-modal';
    overlay.style.cssText = 'position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;font-family:Lato,sans-serif;';

    overlay.innerHTML = [
      '<div style="background:#EFF0F7;border-radius:20px;width:460px;max-width:92vw;max-height:90vh;overflow-y:auto;position:relative;">',
      '  <div style="padding:30px 30px 0;">',
      '    <div style="display:flex;justify-content:space-between;align-items:center;">',
      '      <span style="font-family:Lato,serif;font-size:22px;font-weight:900;color:#090b3b;">Save My Design</span>',
      '      <span id="ksp-save-close" style="cursor:pointer;font-size:14px;color:#4B4F58;font-weight:600;">CLOSE</span>',
      '    </div>',
      '  </div>',
      '  <form id="ksp-save-form" style="padding:20px 30px;">',
      '    <label style="display:block;color:#4B4F58;padding:8px 0 4px;font-size:14px;">Email address<abbr style="color:red;">*</abbr></label>',
      '    <input type="email" name="email" required style="width:100%;padding:10px;border:1px solid #ccc;border-radius:4px;font-size:14px;box-sizing:border-box;">',
      '    <label style="display:block;color:#4B4F58;padding:8px 0 4px;font-size:14px;">Contact number<abbr style="color:red;">*</abbr></label>',
      '    <input type="text" name="contact" required pattern="^[0-9+ ]*$" style="width:100%;padding:10px;border:1px solid #ccc;border-radius:4px;font-size:14px;box-sizing:border-box;">',
      '    <label style="display:block;color:#4B4F58;padding:8px 0 4px;font-size:14px;">Name<abbr style="color:red;">*</abbr></label>',
      '    <input type="text" name="name" required style="width:100%;padding:10px;border:1px solid #ccc;border-radius:4px;font-size:14px;box-sizing:border-box;">',
      '    <label style="display:block;color:#4B4F58;padding:8px 0 4px;font-size:14px;">Postcode<abbr style="color:red;">*</abbr></label>',
      '    <input type="text" name="postcode" required pattern="^[0-9]*$" style="width:100%;padding:10px;border:1px solid #ccc;border-radius:4px;font-size:14px;box-sizing:border-box;">',
      '    <label style="display:block;color:#4B4F58;padding:8px 0 4px;font-size:14px;">Suburb<abbr style="color:red;">*</abbr></label>',
      '    <input type="text" name="suburb" required style="width:100%;padding:10px;border:1px solid #ccc;border-radius:4px;font-size:14px;box-sizing:border-box;">',
      '    <label style="display:block;color:#4B4F58;padding:8px 0 4px;font-size:14px;">State</label>',
      // Clark only delivers to NSW / VIC / QLD, so the inherited all-of-Australia
      // list is trimmed to those three (Ivan Siah, 2026-07-23).
      '    <select name="state" style="width:100%;padding:10px;border:1px solid #ccc;border-radius:4px;font-size:14px;box-sizing:border-box;background:#fff;">',
      '      <option value="New South Wales" selected>New South Wales</option>',
      '      <option value="Victoria">Victoria</option>',
      '      <option value="Queensland">Queensland</option>',
      '    </select>',
      '    <p id="ksp-save-error" style="color:red;font-size:13px;display:none;margin:8px 0 0;"></p>',
      '    <div id="ksp-save-loader" style="display:none;text-align:center;padding:16px 0;">',
      '      <div style="width:32px;height:32px;border:3px solid #EFF0F7;border-top-color:#FFC320;border-radius:50%;animation:expivi-spin 0.8s linear infinite;margin:0 auto;"></div>',
      '    </div>',
      '  </form>',
      '  <div style="padding:0 30px 30px;">',
      '    <button id="ksp-save-submit" type="button" style="width:100%;padding:14px;background:#304C33;color:#fff;border:none;border-radius:30px;font-size:15px;font-weight:600;cursor:pointer;font-family:Lato,sans-serif;">Save Your Tank Design and Get A Quote</button>',
      '  </div>',
      '</div>'
    ].join('');

    doc.body.appendChild(overlay);

    // Close handlers
    doc.getElementById('ksp-save-close').addEventListener('click', function () {
      overlay.style.display = 'none';
    });
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) overlay.style.display = 'none';
    });

    // Submit handler
    doc.getElementById('ksp-save-submit').addEventListener('click', function () {
      var form = doc.getElementById('ksp-save-form');
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      var errorEl = doc.getElementById('ksp-save-error');
      var loader = doc.getElementById('ksp-save-loader');
      var submitBtn = doc.getElementById('ksp-save-submit');
      errorEl.style.display = 'none';
      loader.style.display = 'block';
      submitBtn.disabled = true;
      submitBtn.style.opacity = '0.6';

      // Collect user data
      var formData = {};
      var inputs = form.querySelectorAll('input, select');
      inputs.forEach(function (inp) { formData[inp.name] = inp.value; });

      // Save configuration via Expivi API
      var savePromise;
      if (win.expivi && typeof win.expivi.saveConfiguration === 'function') {
        savePromise = win.expivi.saveConfiguration();
      } else {
        savePromise = Promise.resolve(null);
      }

      savePromise.then(function (configData) {
        // Extract attributes using shared utility
        var extracted = window.ExpiviEmailUtils.extract(configData);

        // Send to Make.com webhook — scenario "Clark Tanks | Save Design" (9609591),
        // cloned from "Kingspan | Save Design" (8940220) with its OWN webhook so the two
        // brands never share a flow. The payload below is unchanged from Kingspan because
        // the cloned scenario still reads the same pills.
        var webhookUrl = 'https://hook.eu2.make.com/k7e075psep5vd7alw2di7asmbn0dy0g2';
        return fetch(webhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            contact: formData.contact,
            postcode: formData.postcode,
            suburb: formData.suburb,
            state: formData.state,
            page_url: window.location.origin + window.location.pathname,
            // Ready-made "View Your Saved Tank" link, used by the email instead of
            // gluing {{page_url}}?configuration={{bundle_uuid}} together in Make.
            // Two reasons the glued version is not enough:
            //   - it assumes page_url has no query string, but Shopify's preview
            //     domain puts the product identity there (/products_preview?preview_key=…),
            //     so dropping it made every link mailed from a preview unusable;
            //   - any existing `configuration` has to be removed, otherwise saving
            //     again from an already-restored page yields two of them.
            saved_url: (function () {
              var u = new URL(window.location.href);
              u.hash = '';
              u.searchParams.delete('configuration');
              u.searchParams.set('configuration', extracted.bundleUuid || '');
              return u.toString();
            })(),
            bundle_uuid: extracted.bundleUuid,
            attributes_html: extracted.attributesHtml,
            attributes_text: extracted.attributesText,
            // Send the configuration as a JSON STRING, not an object. The make.com scenario
            // writes this field straight into a text file ("Create a File from Text") and uploads
            // it to DigitalOcean; a nested object/collection can't be serialised to text there
            // and ends up stored as the literal "undefined", breaking "View your saved tank".
            configuration: (typeof configData === 'string' ? configData : JSON.stringify(configData))
          })
        });
      }).then(function () {
        // Show success
        loader.style.display = 'none';
        overlay.querySelector('div').innerHTML = [
          '<div style="padding:30px;">',
          '  <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:20px;">',
          '    <span style="font-family:Lato,serif;font-size:22px;font-weight:900;color:#304C33;">Congratulations</span>',
          '    <span id="ksp-success-close" style="cursor:pointer;font-size:14px;color:#4B4F58;font-weight:600;">CLOSE</span>',
          '  </div>',
          '  <p style="color:#4B4F58;font-size:15px;line-height:1.6;">Thank you for submitting your tank design. One of our friendly team members will be in touch soon.</p>',
          '  <button id="ksp-start-over" type="button" style="width:100%;padding:14px;background:transparent;color:#304C33;border:2px solid #304C33;border-radius:30px;font-size:15px;font-weight:600;cursor:pointer;margin-top:16px;font-family:Lato,sans-serif;">Start over</button>',
          '</div>'
        ].join('');

        doc.getElementById('ksp-success-close').addEventListener('click', function () {
          overlay.style.display = 'none';
        });
        doc.getElementById('ksp-start-over').addEventListener('click', function () {
          overlay.style.display = 'none';
          if (win.location) win.location.reload();
        });
      }).catch(function (err) {
        console.error('Save Design error:', err);
        loader.style.display = 'none';
        submitBtn.disabled = false;
        submitBtn.style.opacity = '1';
        errorEl.textContent = 'Failed to save design. Please try again.';
        errorEl.style.display = 'block';
      });
    });
  }

  // ─── Viewer UI button (AR only) ──────────────────────────
  // Ported from WooCommerce xpv-custom.js.
  // Injected into the iframe's #viewer-container after the 3D canvas loads.
  // Environment and Model buttons removed for Clark (see below).

  function injectViewerButtons(doc, win) {
    if (!doc) return;
    var attempts = 0;
    var maxAttempts = 60; // 30s

    var interval = win.setInterval(function () {
      attempts++;
      var viewerContainer = doc.getElementById('expivi-viewer-container') || doc.getElementById('viewer-container');
      var canvas = doc.querySelector('canvas');
      if (!viewerContainer || !canvas) {
        if (attempts >= maxAttempts) win.clearInterval(interval);
        return;
      }
      if (doc.querySelector('.iconsUI')) { win.clearInterval(interval); return; } // already injected
      win.clearInterval(interval);

      var icons = doc.createElement('div');
      icons.className = 'iconsUI';
      icons.style.position = 'fixed';
      icons.style.top = '20px';
      icons.style.left = '20px';
      icons.style.margin = '0';
      // Only AR. The Environment (background) and Model (scale figure) buttons
      // were dropped at Ivan Siah's request (2026-08-04): "Can you hide the
      // background and model options please? They're not relevant to the Clark
      // Tank's range." Removed outright rather than hidden with CSS, so no dead
      // handler keeps writing att-background / att-person on a catalogue that
      // does not use them.
      icons.innerHTML = [
        // AR
        '<a class="ui-button singleIcons" id="arButton" style="cursor:pointer;">',
        '  <span class="ui-icon-svg"><svg viewBox="8 3 26 30" width="18" height="18" style="stroke:white;fill:none;stroke-width:2;stroke-linejoin:round;stroke-linecap:round;">',
        '    <path d="M21 17L10 11L21 5L32 11L21 17L32 11V24L21 31V17V31L10 24V11L21 17Z"></path>',
        '  </svg></span>',
        '  <span class="ui-text" style="color:white !important;">Augmented Reality</span>',
        '</a>'
      ].join('');
      viewerContainer.appendChild(icons);

      // AR — triggers Expivi's hidden native AR button
      doc.getElementById('arButton').addEventListener('click', function () {
        var nativeAr = doc.getElementById('xpv-toolbar-item-web-ar');
        if (nativeAr) nativeAr.click();
      });
    }, 500);
  }

  // ─── View in 3D: show only Region + Colour steps ─────────
  // Waits for the Vuetify stepper to render, then:
  //   1. Hides every step whose header is NOT "Choose region" or "Colour"
  //   2. Sets editable + complete on visible steps so the user can click freely

  var VIEW_STEPS = ['choose region', 'colour'];

  function filterStepsForViewMode(doc, win) {
    if (!doc) return;
    var attempts = 0;
    var maxAttempts = 40; // 40 × 500ms = 20s

    var interval = win.setInterval(function () {
      attempts++;
      var stepContainers = doc.querySelectorAll('.v-stepper--vertical > .container');
      if (stepContainers.length === 0) {
        if (attempts >= maxAttempts) win.clearInterval(interval);
        return;
      }
      win.clearInterval(interval);

      stepContainers.forEach(function (container) {
        var header = container.querySelector('.step-header-name');
        var name = header ? header.textContent.trim().toLowerCase() : '';
        var keep = VIEW_STEPS.some(function (s) { return name.indexOf(s) !== -1; });
        if (!keep) {
          container.classList.add('ksp-view-hidden');
        }
      });

    }, 500);
  }

  // ─── Modal open/close ─────────────────────────────────────

  function showLoading() {
    if (loadingEl) {
      loadingEl.style.display = 'flex';
      loadingEl.innerHTML = '<div class="expivi-spinner"></div><p>Loading 3D configurator...</p>';
    }
    iframe.style.display = 'none';
  }

  function hideLoading() {
    if (loadingEl) loadingEl.style.display = 'none';
    iframe.style.display = 'block';
  }

  // "Adding to cart…" overlay shown inside the configurator iframe while the native
  // add + replica/variant creation runs (a few seconds), so the click doesn't feel frozen.
  function showAddingOverlay(doc) {
    if (!doc || doc.getElementById('ksp-adding-overlay')) return;
    if (!doc.getElementById('ksp-adding-style')) {
      var st = doc.createElement('style');
      st.id = 'ksp-adding-style';
      st.textContent = '@keyframes ksp-spin{to{transform:rotate(360deg)}}';
      doc.head.appendChild(st);
    }
    var ov = doc.createElement('div');
    ov.id = 'ksp-adding-overlay';
    ov.style.cssText = 'position:fixed;inset:0;z-index:99999;background:rgba(255,255,255,.88);display:flex;flex-direction:column;align-items:center;justify-content:center;font-family:Lato,Helvetica,Arial,sans-serif;';
    ov.innerHTML = '<div style="width:46px;height:46px;border:4px solid #d9dbe4;border-top-color:#304C33;border-radius:50%;animation:ksp-spin .8s linear infinite;"></div>' +
                   '<p style="margin-top:16px;color:#304C33;font-weight:600;font-size:16px;">Adding to cart…</p>';
    doc.body.appendChild(ov);
  }

  function hideAddingOverlay(doc) {
    if (!doc) return;
    var ov = doc.getElementById('ksp-adding-overlay');
    if (ov) ov.remove();
  }

  // ─── Summary modal (Configuration overview) ────────────────
  // Mirrors the WooCommerce reference: shows the user's selections in a list
  // before committing to Add to Cart. Built from configData via ExpiviEmailUtils.

  function showSummaryModal(doc, win, onConfirm) {
    if (!doc) return;
    if (doc.getElementById('ksp-summary-overlay')) return;

    // Inject styles once
    if (!doc.getElementById('ksp-summary-style')) {
      var st = doc.createElement('style');
      st.id = 'ksp-summary-style';
      st.textContent = [
        '#ksp-summary-overlay { position:fixed; inset:0; z-index:99998; background:rgba(0,0,0,.45); display:flex; align-items:center; justify-content:center; padding:20px; font-family:Lato,Helvetica,Arial,sans-serif; }',
        '.ksp-summary-modal { background:#fff; border-radius:8px; width:100%; max-width:1000px; max-height:90vh; display:flex; flex-direction:column; overflow:hidden; box-shadow:0 20px 60px rgba(0,0,0,.3); }',
        '.ksp-summary-head { display:flex; align-items:center; justify-content:space-between; padding:18px 28px; border-bottom:1px solid #eee; }',
        '.ksp-summary-title { font-family:Lato,serif; font-size:20px; font-weight:700; color:#304C33; margin:0; }',
        '.ksp-summary-close { background:none; border:none; font-size:28px; line-height:1; color:#888; cursor:pointer; padding:0 4px; }',
        '.ksp-summary-close:hover { color:#304C33; }',
        // Body becomes a 2-column grid: table left, product image right
        '.ksp-summary-body { padding:18px 28px; overflow-y:auto; flex:1 1 auto; color:#333; font-size:14px; display:grid; grid-template-columns:1fr 320px; gap:24px; align-items:start; }',
        '.ksp-summary-config { min-width:0; }',
        '.ksp-summary-preview { display:flex; align-items:center; justify-content:center; background:#f7f8fa; border-radius:6px; padding:16px; min-height:280px; }',
        '.ksp-summary-preview img { max-width:100%; max-height:340px; object-fit:contain; display:block; }',
        '.ksp-summary-preview-empty { color:#888; font-size:13px; }',
        '.ksp-summary-body ul { margin:0; padding-left:20px; }',
        '.ksp-summary-body li { padding:4px 0; }',
        '.ksp-summary-body table { width:100%; border-collapse:collapse; }',
        '.ksp-summary-body table td:first-child { font-weight:600; color:#555; padding:8px 12px; border-bottom:1px solid #eee; }',
        '.ksp-summary-body table td:last-child { color:#333; padding:8px 12px; border-bottom:1px solid #eee; }',
        '.ksp-summary-empty { color:#888; padding:20px 0; text-align:center; }',
        '.ksp-summary-actions { padding:18px 28px; border-top:1px solid #eee; display:flex; gap:12px; justify-content:flex-end; }',
        '.ksp-summary-btn { padding:0 24px; height:42px; border-radius:30px; font-size:14px; font-weight:600; cursor:pointer; border:2px solid #304C33; }',
        '.ksp-summary-btn--outline { background:transparent; color:#304C33; }',
        '.ksp-summary-btn--outline:hover { background:#304C33; color:#fff; }',
        '.ksp-summary-btn--primary { background:#304C33; color:#fff; }',
        '.ksp-summary-loading { display:flex; align-items:center; justify-content:center; padding:40px 0; color:#304C33; font-weight:600; grid-column:1 / -1; }',
        // Mobile: stack vertically — table first, product image after
        '@media (max-width: 768px) {',
        '  .ksp-summary-body { grid-template-columns:1fr; gap:16px; padding:14px 18px; }',
        '  .ksp-summary-config { order:1; }',
        '  .ksp-summary-preview { order:2; min-height:220px; padding:12px; }',
        '  .ksp-summary-preview img { max-height:240px; }',
        '  .ksp-summary-head { padding:14px 18px; }',
        '  .ksp-summary-actions { padding:14px 18px; }',
        '}'
      ].join('\n');
      doc.head.appendChild(st);
    }

    // Two modes:
    //   - With onConfirm callback: show "Cancel" + "Add to Cart" (used by ATC flow)
    //   - Without callback (preview): show only "Close" (used by Summary tab in stepbar)
    var previewOnly = typeof onConfirm !== 'function';

    // Snapshot the current 3D viewer as a data URL.
    // Primary: window.expivi.getScreenshot() — Expivi API that returns a JPEG
    //          data URL of the current render (works regardless of how WebGL was
    //          initialised, since Expivi renders into a separate framebuffer).
    // Fallback: canvas.toDataURL() — only works if preserveDrawingBuffer:true,
    //          which Expivi does NOT set by default, so this usually returns black.
    function fallbackCanvasCapture() {
      try {
        var canvases = doc.querySelectorAll('canvas');
        for (var i = 0; i < canvases.length; i++) {
          var c = canvases[i];
          if (!c.width || !c.height) continue;
          var url2 = c.toDataURL('image/png');
          if (url2 && url2.length > 'data:image/png;base64,'.length + 200) {
            return url2;
          }
        }
      } catch (e) { /* tainted canvas / WebGL no-readback — ignore */ }
      return '';
    }
    // Cancels the (async, retrying) screenshot capture the instant the summary is
    // confirmed or closed — CRITICAL: the native Add to Cart runs its OWN
    // saveConfiguration(), and a getScreenshot() retry still in flight collides with
    // it, making the first Add-to-Cart click a no-op (worked only on the 2nd try).
    var captureCancelled = false;

    // Returns a Promise<string> — the JPEG data URL, or '' when unavailable.
    // Newer Expivi (options "latest") returns a PROMISE from getScreenshot() and only
    // yields a frame once the viewer has rendered; older builds returned the data-URL
    // synchronously. Promise.resolve handles both; retry briefly until a frame is ready.
    function captureViewerImage(attempt) {
      attempt = attempt || 0;
      var MAX = 12; // ~6s
      function retryOrFallback() {
        var fb = fallbackCanvasCapture();
        if (fb) return fb;
        if (!captureCancelled && attempt < MAX) {
          return new Promise(function (res) { setTimeout(res, 500); })
            .then(function () { return captureCancelled ? '' : captureViewerImage(attempt + 1); });
        }
        return '';
      }
      if (captureCancelled) return Promise.resolve('');
      try {
        if (win.expivi && typeof win.expivi.getScreenshot === 'function') {
          return Promise.resolve(win.expivi.getScreenshot())
            .then(function (url) {
              return (typeof url === 'string' && url.indexOf('data:image') === 0)
                ? url : retryOrFallback();
            })
            .catch(function () { return retryOrFallback(); });
        }
      } catch (e) { /* API not ready — fall through */ }
      return Promise.resolve(retryOrFallback());
    }

    function build(configHtml, imgUrl) {
      var overlay = doc.createElement('div');
      overlay.id = 'ksp-summary-overlay';
      var actionsHtml = previewOnly
        ? '<button class="ksp-summary-btn ksp-summary-btn--primary" id="ksp-summary-confirm" type="button">Close</button>'
        : '<button class="ksp-summary-btn ksp-summary-btn--outline" id="ksp-summary-cancel" type="button">Cancel</button>' +
          '<button class="ksp-summary-btn ksp-summary-btn--primary" id="ksp-summary-confirm" type="button">Add to Cart</button>';
      var previewHtml = imgUrl
        ? '<img src="' + imgUrl + '" alt="Your tank">'
        : '<span class="ksp-summary-preview-empty">3D preview unavailable</span>';
      overlay.innerHTML = [
        '<div class="ksp-summary-modal" role="dialog" aria-modal="true" aria-labelledby="ksp-summary-title">',
        '  <div class="ksp-summary-head">',
        '    <h2 class="ksp-summary-title" id="ksp-summary-title">Configuration overview</h2>',
        '    <button class="ksp-summary-close" type="button" aria-label="Close">&times;</button>',
        '  </div>',
        '  <div class="ksp-summary-body">',
        '    <div class="ksp-summary-config">' + configHtml + '</div>',
        '    <div class="ksp-summary-preview">' + previewHtml + '</div>',
        '  </div>',
        '  <div class="ksp-summary-actions">' + actionsHtml + '</div>',
        '</div>'
      ].join('');
      doc.body.appendChild(overlay);

      function close() { captureCancelled = true; overlay.remove(); }
      overlay.querySelector('.ksp-summary-close').addEventListener('click', close);
      var cancelBtn = overlay.querySelector('#ksp-summary-cancel');
      if (cancelBtn) cancelBtn.addEventListener('click', close);
      overlay.querySelector('#ksp-summary-confirm').addEventListener('click', function () {
        close();
        if (!previewOnly) onConfirm();
      });
      overlay.addEventListener('click', function (e) {
        if (e.target === overlay) close();
      });
    }

    // Save current configuration via Expivi API to get the attribute list.
    // On the options "latest" channel, saveConfiguration() can throw
    // "Resolved called before initialization" when called before the viewer has
    // finished initialising — retry briefly until it resolves (the native ATC waits too).
    function saveConfigWhenReady() {
      if (!(win.expivi && typeof win.expivi.saveConfiguration === 'function')) {
        return Promise.resolve(null);
      }
      var attempt = 0, maxAttempts = 40; // ~20s
      function once() {
        attempt++;
        return Promise.resolve()
          .then(function () { return win.expivi.saveConfiguration(); })
          .catch(function (err) {
            var msg = String((err && (err.message || err)) || '');
            if (attempt < maxAttempts && /initial|not ready|before init/i.test(msg)) {
              return new Promise(function (res) { setTimeout(res, 500); }).then(once);
            }
            throw err;
          });
      }
      return once();
    }
    var savePromise = saveConfigWhenReady();

    // Show modal immediately with a loading state; the 3D preview + config table
    // fill in asynchronously (both Expivi APIs are async on the "latest" channel).
    build('<div class="ksp-summary-loading">Loading your configuration…</div>', '');

    savePromise.then(function (configData) {
      var html = '';
      try {
        var extracted = window.ExpiviEmailUtils && window.ExpiviEmailUtils.extract(configData);
        if (extracted && extracted.attributesHtml) {
          html = extracted.attributesHtml;
        }
      } catch (e) { /* fall through */ }
      if (!html) html = '<div class="ksp-summary-empty">No selections to show.</div>';

      var overlay = doc.getElementById('ksp-summary-overlay');
      if (!overlay) return; // user closed before save finished
      var cfgEl = overlay.querySelector('.ksp-summary-config');
      if (cfgEl) cfgEl.innerHTML = html;

      // 3D preview — capture AFTER saveConfiguration has settled (serialized, never
      // concurrent with an Expivi API call) and only while the summary is still open.
      // Cancelled the moment the user confirms, so it can't clash with the native ATC.
      captureViewerImage().then(function (imgUrl) {
        if (!imgUrl || captureCancelled) return;
        var ov = doc.getElementById('ksp-summary-overlay');
        if (!ov) return;
        var prev = ov.querySelector('.ksp-summary-preview');
        if (prev) prev.innerHTML = '<img src="' + imgUrl + '" alt="Your tank">';
      });
    }).catch(function () {
      var overlay = doc.getElementById('ksp-summary-overlay');
      if (!overlay) return;
      var cfgEl = overlay.querySelector('.ksp-summary-config');
      if (cfgEl) cfgEl.innerHTML = '<div class="ksp-summary-empty">Could not load configuration.</div>';
    });
  }

  // Expose a preview-only entry point for other scripts. Shows the same modal as
  // Add to Cart, but the primary action is "Close" — no cart side-effects.
  window.ClarkExpivi = window.ClarkExpivi || {};
  window.ClarkExpivi.previewSummary = function () {
    if (!iframe) return;
    var doc, win;
    try { doc = iframe.contentDocument; win = iframe.contentWindow; } catch (e) { return; }
    if (!doc || !win) return;
    showSummaryModal(doc, win, null);
  };

  /**
   * @param {boolean} fullscreen - true = Build in 3D (no preset), false = View in 3D (with preset)
   */
  function openModal(fullscreen) {
    if (!baseUrl) {
      console.warn('Expivi: No configurator URL found on page');
      return;
    }

    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Both modes use fullscreen modal
    if (modalInner) {
      modalInner.classList.add('expivi-modal--fullscreen');
    }

    showLoading();

    // CSS injected in "View in 3D" mode:
    //   - Hide bottom bar, tabs, nav buttons, and non-relevant steps
    //   - Flatten the stepper: no circles, no connectors, all visible contents stacked
    var viewOnlyCss = fullscreen ? '' : [
      '<style>',
      '.bottom-content { display: none !important; }',
      '.expiviWrapper { display: none !important; }',
      '.ksp-view-hidden { display: none !important; }',
      '.step-btn-next, .step-btn-prev { display: none !important; }',
      /* Remove step number circles */
      '.v-stepper__step__step { display: none !important; }',
      /* Remove step header icon (info) */
      '.step-header-icon { display: none !important; }',
      /* Remove vertical connector line and left margin */
      '.v-stepper--vertical .v-stepper__content { border-left: none !important; margin-left: 0 !important; padding-left: 12px !important; }',
      /* Force ALL step contents visible (hidden steps won\'t show — parent has display:none) */
      '.v-stepper__content { display: block !important; }',
      '.v-stepper__wrapper { height: auto !important; transition: none !important; }',
      /* Step header as simple section label */
      '.v-stepper__step { padding-left: 0 !important; cursor: default !important; }',
      /* Padding for the options panel so content doesn\'t touch edges */
      '.configurator-container { padding: 16px 20px !important; }',
      '</style>'
    ].join('');

    // CSS injected in "Build in 3D" (fullscreen) mode on the PDP modal:
    // fit the modal to the viewport height and scroll the OPTIONS panel internally,
    // so the whole document doesn't scroll and leave a scrollbar strip cutting the
    // right edge. Excluded on the standalone full-page builder (data-stepbar="1"),
    // which has its own layout + top step bar.
    var isStepbarPage = modal.getAttribute('data-stepbar') === '1';
    var fullscreenCss = (fullscreen && !isStepbarPage) ? [
      '<style>',
      '.expivi-page-container { height: 100vh !important; overflow: hidden !important; }',
      '.configurator-container { height: 100vh !important; overflow-y: auto !important; overflow-x: hidden !important; }',
      '.configurator-container::-webkit-scrollbar { width: 8px; }',
      '.configurator-container::-webkit-scrollbar-thumb { background: rgba(0,0,0,.25); border-radius: 4px; }',
      '</style>'
    ].join('') : '';

    fetch(baseUrl)
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var inject = '<base href="' + window.location.origin + '/">' +
          (cssUrl ? '<link rel="stylesheet" href="' + cssUrl + '">' : '') +
          viewOnlyCss +
          fullscreenCss;

        if (html.indexOf('<head>') !== -1) {
          html = html.replace('<head>', '<head>' + inject);
        } else if (html.indexOf('<HEAD>') !== -1) {
          html = html.replace('<HEAD>', '<HEAD>' + inject);
        } else {
          html = '<!DOCTYPE html><html><head>' + inject + '</head><body>' + html + '</body></html>';
        }

        iframe.srcdoc = html;
        iframe.onload = function () {
          hideLoading();
          // Apply the SKU preset in BOTH "View" and "Build" modes,
          // but never clobber a saved-config auto-open (?configuration=).
          if (presetKey && !(fullscreen && savedConfig)) {
            applyPreset(presetKey);
          }
          // Load saved configuration if opened from email link
          if (fullscreen && savedConfig) {
            loadSavedConfiguration(savedConfig);
          }
          if (!fullscreen) {
            filterStepsForViewMode(iframe.contentDocument, iframe.contentWindow);
          }
          if (fullscreen) {
            injectViewerButtons(iframe.contentDocument, iframe.contentWindow);
            injectUspRow(iframe.contentDocument);
            injectBottomBar(iframe.contentDocument, iframe.contentWindow);
          }
          injectLateStyles(iframe.contentDocument, iframe.contentWindow);
        };
      })
      .catch(function (err) {
        console.error('Expivi: Failed to load configurator:', err);
        if (loadingEl) {
          loadingEl.innerHTML = '<p style="color:#666;font-size:14px;">Failed to load configurator.<br>Please try again.</p>';
        }
      });
  }

  function closeModal() {
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    iframe.srcdoc = '';
    iframe.onload = null;
    if (modalInner) modalInner.classList.remove('expivi-modal--fullscreen');
  }

  // ─── Positioning ──────────────────────────────────────────

  var buttonsRow = document.querySelector('.expivi-buttons-row');
  var priceEl    = document.querySelector('product-price');
  if (buttonsRow && priceEl) {
    var priceWrapper = document.createElement('div');
    priceWrapper.className = 'expivi-price-buttons-row';
    priceEl.parentElement.insertBefore(priceWrapper, priceEl);
    priceWrapper.appendChild(priceEl);
    priceWrapper.appendChild(buttonsRow);
  }

  // ─── Event listeners ──────────────────────────────────────

  if (buildTrigger) buildTrigger.addEventListener('click', function () { openModal(true); });
  if (viewTrigger)  viewTrigger.addEventListener('click',  function () { openModal(false); });

  closeBtn.addEventListener('click', closeModal);

  modal.addEventListener('click', function (e) {
    if (e.target === modal) closeModal();
  });

  // ─── Auto-open from saved configuration link ──────────────
  // When the URL contains ?configuration=UUID (from the email link),
  // append it to baseUrl and auto-open the Build in 3D modal.
  var pageParams = new URLSearchParams(window.location.search);
  var savedConfig = pageParams.get('configuration');
  if (savedConfig && baseUrl) {
    // Auto-open Build in 3D modal — savedConfig is used inside openModal
    // to call loadSavedConfiguration() after the iframe loads
    setTimeout(function () { openModal(true); }, 800);
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') {
      closeModal();
    }
  });
})();
